import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Zap, ArrowLeft, Download, ArrowRight, Search, CheckCircle, Lock, Lightbulb, MessageCircle, X, RefreshCcw, Crown, Building2, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

// Définition des 5 parties visibles (la partie contexte est faite en onboarding)
// Note: La partie "Légende" (partie 5) est le Voyage du Héros - BONUS
const PARTS_CONFIG = [
  { id: 1, internalId: 2, name: 'Avatar', fullName: 'Création de l\'avatar', steps: 23, color: '#40d0d3', icon: '👤' },
  { id: 2, internalId: 3, name: 'Concurrence', fullName: 'Analyse concurrentielle', steps: 10, color: '#ff8ce9', icon: '🔍' },
  { id: 3, internalId: 4, name: 'Disruptif', fullName: 'Marketing disruptif', steps: 11, color: '#ffc239', icon: '💥' },
  { id: 4, internalId: 5, name: 'Big Idea', fullName: 'La Big Idea', steps: 3, color: '#40d0d3', icon: '💡' },
  { id: 5, internalId: 6, name: 'BONUS', fullName: 'Voyage du Héros (Bonus)', steps: 3, color: '#9b59b6', icon: '🦸', isBonus: true }
];

const ONBOARDING_STEPS = 8; // Questions de contexte faites en onboarding

// Calculer les plages d'étapes pour chaque partie (sans onboarding)
const getPartRanges = () => {
  let currentStep = 1;
  return PARTS_CONFIG.map(part => {
    const range = { start: currentStep, end: currentStep + part.steps - 1 };
    currentStep += part.steps;
    return { ...part, ...range };
  });
};

const PARTS = getPartRanges();
const TOTAL_STEPS = 50; // 50 étapes au total (affichage)

// Déterminer la partie actuelle basée sur le numéro d'étape
const getCurrentPartInfo = (step) => {
  // step = étape dans la DB (commence à 1 après onboarding)
  for (const part of PARTS) {
    if (step >= part.start && step <= part.end) {
      return {
        partId: part.id,
        partName: part.fullName,
        stepInPart: step - part.start + 1,
        totalStepsInPart: part.steps,
        color: part.color,
        icon: part.icon
      };
    }
  }
  // Si au-delà, retourner la dernière partie
  const lastPart = PARTS[PARTS.length - 1];
  return {
    partId: lastPart.id,
    partName: lastPart.fullName,
    stepInPart: lastPart.steps,
    totalStepsInPart: lastPart.steps,
    color: lastPart.color,
    icon: lastPart.icon
  };
};

// Grouper les messages par partie
const groupMessagesByPart = (messages) => {
  const grouped = {};
  PARTS.forEach(part => {
    grouped[part.id] = [];
  });
  
  messages.forEach((msg, index) => {
    if (msg.role === 'assistant') {
      // Essayer d'extraire le numéro d'étape du contenu
      const stepMatch = msg.content.match(/ÉTAPE\s*(\d+)/i);
      if (stepMatch) {
        const stepNum = parseInt(stepMatch[1]);
        // Déterminer dans quelle partie cette étape se trouve
        // Partie 1 (Avatar): étapes 1-23
        // Partie 2 (Concurrence): étapes 24-33
        // Partie 3 (Disruptif): étapes 34-44
        // Partie 4 (Big Idea): étapes 45-47
        // Partie 5 (Légende/Voyage du Héros): étapes 48-50
        let partId = 1;
        if (stepNum <= 23) partId = 1;
        else if (stepNum <= 33) partId = 2;
        else if (stepNum <= 44) partId = 3;
        else if (stepNum <= 47) partId = 4;
        else partId = 5; // Légende (Voyage du Héros)
        
        // Sécurité : vérifier que la partie existe
        if (grouped[partId]) {
          grouped[partId].push({ ...msg, stepNum, index });
        } else {
          grouped[1].push({ ...msg, stepNum, index });
        }
      } else {
        // Message sans numéro d'étape (intro, transition)
        grouped[1].push({ ...msg, index });
      }
    }
  });
  
  return grouped;
};

const Study = () => {
  const { studyId } = useParams();
  const [study, setStudy] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activePart, setActivePart] = useState(1);
  const [subscription, setSubscription] = useState(null);
  const [showDeepenModal, setShowDeepenModal] = useState(false);
  const [showQuestionInput, setShowQuestionInput] = useState(false);
  const [customQuestion, setCustomQuestion] = useState('');
  const [deepenLoading, setDeepenLoading] = useState(false);
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  
  const FREE_STEPS_LIMIT = 3; // Limite pour les utilisateurs gratuits
  const isPaidUser = subscription?.plan && subscription.plan !== 'free';
  const isTrialUser = subscription?.is_trial || subscription?.status === 'trialing';
  const isTrialNoCB = subscription?.plan === 'trial_no_cb' || subscription?.status === 'trial_no_cb';
  const isPromoNoCB = subscription?.plan?.startsWith('promo_') || subscription?.status === 'promo_no_cb';
  // Seuls les essais 7 jours SANS CB sont bloqués pour le PDF
  // Les codes promo (avec ou sans CB) ont accès au PDF
  const canDownloadPDF = !isTrialNoCB;

  // Protection contre la copie/sélection/impression pour les utilisateurs en essai sans CB (7 jours)
  // Note: Les utilisateurs avec code promo n'ont PAS ces restrictions
  useEffect(() => {
    if (!isTrialNoCB || isPromoNoCB) return;

    // Désactiver le clic droit
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Désactiver la sélection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Désactiver les raccourcis clavier (Ctrl+C, Ctrl+P, Ctrl+S, PrintScreen)
    const handleKeyDown = (e) => {
      // Ctrl+C (copier), Ctrl+P (imprimer), Ctrl+S (sauvegarder), Ctrl+A (tout sélectionner)
      if (e.ctrlKey && (e.key === 'c' || e.key === 'C' || e.key === 'p' || e.key === 'P' || e.key === 's' || e.key === 'S' || e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        return false;
      }
      // PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        return false;
      }
    };

    // Désactiver le copier
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Désactiver le couper
    const handleCut = (e) => {
      e.preventDefault();
      return false;
    };

    // Ajouter les event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);

    // Ajouter les styles CSS pour désactiver la sélection
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    document.body.style.MozUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.MozUserSelect = '';
    };
  }, [isTrialNoCB]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    fetchData();
  }, [studyId, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activePart]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [studyRes, historyRes, subRes] = await Promise.all([
        axios.get(`${API}/studies/${studyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/chat/history/${studyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/subscriptions/current`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStudy(studyRes.data);
      setMessages(historyRes.data.messages);
      setSubscription(subRes.data);
      
      // IMPORTANT: Pour les utilisateurs gratuits OU si l'étude n'a pas encore commencé,
      // toujours afficher la Partie 1
      const hasMessages = historyRes.data.messages && historyRes.data.messages.length > 0;
      if (!hasMessages || studyRes.data.current_step === 0) {
        setActivePart(1);
      } else {
        // Déterminer la partie active basée sur l'étape actuelle
        const partInfo = getCurrentPartInfo(studyRes.data.current_step);
        setActivePart(partInfo.partId);
      }
      
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/auth');
      } else if (error.response?.status === 404) {
        toast.error('Étude introuvable');
        navigate('/dashboard');
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const handleDownloadPDF = async (partNumber = null) => {
    // Vérifier si l'utilisateur peut télécharger le PDF
    if (!canDownloadPDF) {
      toast.error('Les exports PDF ne sont pas disponibles pendant la période d\'essai. Passez à un abonnement complet pour débloquer cette fonctionnalité.', { duration: 5000 });
      return;
    }
    
    const token = localStorage.getItem('token');
    setDownloadingPdf(true);

    try {
      const response = await axios.get(
        `${API}/studies/${studyId}/download-pdf${partNumber ? `?part_number=${partNumber}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `boost-strategia_partie${partNumber || 'complete'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF téléchargé avec succès !');
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 403) {
        toast.error('Les exports PDF ne sont pas disponibles pendant la période d\'essai.', { duration: 5000 });
      } else {
        toast.error('Erreur lors du téléchargement du PDF');
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  const sendMessage = async (messageText) => {
    if (loading) return;

    setLoading(true);

    setMessages(prev => [...prev, {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    }]);

    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(
        `${API}/chat/send`,
        {
          message: messageText,
          study_id: studyId
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000  // 3 minutes pour laisser le temps à l'IA de répondre
        }
      );

      const responseContent = response.data.response;
      
      // Ajouter le message de réponse
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: responseContent,
        timestamp: new Date().toISOString()
      }]);

      // Récupérer les données mises à jour
      const studyRes = await axios.get(`${API}/studies/${studyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudy(studyRes.data);
      
      // Vérifier si utilisateur gratuit a atteint la limite
      // NE PAS REDIRIGER - juste afficher un message et bloquer les actions
      if (subscription?.plan === 'free' && studyRes.data.current_step >= FREE_STEPS_LIMIT) {
        toast.success('Bravo ! Tu as terminé ton test gratuit. Découvre nos offres pour aller plus loin !');
        // Ne pas rediriger automatiquement - l'utilisateur peut consulter son étude
        return;
      }
      
      // Check if we should redirect to legend page (step 48)
      // La transition vers la partie 5 se fait à l'étape 48
      if (responseContent.includes('PARTIE 5') && responseContent.includes('12 questions')) {
        // Attendre un peu puis rediriger vers la page légende
        setTimeout(() => {
          navigate(`/study/${studyId}/legend`);
        }, 2500);
        return;
      }
      
      // Mettre à jour la partie active si nécessaire
      const partInfo = getCurrentPartInfo(studyRes.data.current_step);
      setActivePart(partInfo.partId);
      
      // Réinitialiser le compteur de retry après un succès
      setRetryCount(0);
      
    } catch (error) {
      console.error('Send error:', error);
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);
      
      // Même en cas d'erreur, récupérer l'état actuel de l'étude
      // car l'étape peut avoir été enregistrée côté serveur
      try {
        const token = localStorage.getItem('token');
        const [studyRes, historyRes] = await Promise.all([
          axios.get(`${API}/studies/${studyId}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/chat/history/${studyId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setStudy(studyRes.data);
        
        // Mettre à jour les messages depuis le serveur
        const serverMessages = historyRes.data?.messages || historyRes.data || [];
        if (serverMessages.length > 0) {
          setMessages(serverMessages.map(msg => ({
            role: msg.role,
            content: msg.content
          })));
        }
        
        // Vérifier si utilisateur gratuit a atteint la limite
        if (subscription?.plan === 'free' && studyRes.data.current_step >= FREE_STEPS_LIMIT) {
          toast.success('Bravo ! Tu as terminé ton test gratuit. Découvre nos offres pour aller plus loin !');
          setShowRetryButton(false);
          return;
        }
      } catch (fetchError) {
        console.error('Error fetching study state:', fetchError);
      }
      
      if (newRetryCount >= 3) {
        toast.error('3 échecs consécutifs. Vous pouvez passer cette étape ou réessayer. Pas d\'inquiétude : l\'étape est souvent enregistrée et apparaîtra dans votre PDF.', { duration: 6000 });
      } else {
        toast.error(`Erreur réseau (tentative ${newRetryCount}/3). Cliquez sur "Réessayer". L'étape peut avoir été enregistrée malgré l'erreur.`, { duration: 5000 });
      }
      setShowRetryButton(true);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour réessayer après une erreur
  const handleRetry = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    setLoading(true);
    try {
      // D'abord, vérifier et corriger l'étape si nécessaire
      let retryRes;
      try {
        retryRes = await axios.post(`${API}/studies/${studyId}/recover`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (retryError) {
        console.error('Retry API error:', retryError);
        // Continuer même si retry-step échoue
      }
      
      // Recharger l'étude ET les messages
      const [studyRes, historyRes] = await Promise.all([
        axios.get(`${API}/studies/${studyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/chat/history/${studyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (studyRes.data) {
        setStudy(studyRes.data);
      }
      
      if (historyRes.data && Array.isArray(historyRes.data)) {
        setMessages(historyRes.data);
      }
      
      setShowRetryButton(false);
      // NE PAS remettre retryCount à 0 ici - seulement après un succès
      
      // Message clair selon le résultat
      if (retryRes?.data?.step_exists) {
        toast.success('Pas de panique ! Cette étape sera quand même dans votre PDF.', { 
          duration: 5000,
          style: { background: '#ec4899', color: 'white' }
        });
      } else {
        toast.info('Prêt à réessayer. Cliquez sur Continuer.', { duration: 4000 });
      }
      
    } catch (error) {
      console.error('Retry error:', error);
      toast.error('Erreur. Rafraîchissez la page (F5).');
      setShowRetryButton(false);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour passer l'étape après trop d'échecs
  const handleSkipStep = async () => {
    const token = localStorage.getItem('token');
    try {
      // Récupérer l'étape actuelle AVANT de la passer
      const currentStepNumber = study?.current_step + 1 || '?';
      
      await axios.post(`${API}/studies/${studyId}/advance`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Recharger l'étude
      const studyRes = await axios.get(`${API}/studies/${studyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudy(studyRes.data);
      
      // Ajouter un message système pour indiquer l'étape passée (avec le bon numéro)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `⚠️ **Étape ${currentStepNumber} passée** - En raison d'erreurs techniques, cette étape n'a pas pu s'afficher. **Pas d'inquiétude** : elle a probablement été enregistrée et apparaîtra dans votre PDF final. Cliquez sur "Continuer" pour passer à la suite.`,
        timestamp: new Date().toISOString(),
        isSkipMessage: true
      }]);
      
      setShowRetryButton(false);
      setRetryCount(0);
      toast.success(`Étape ${currentStepNumber} passée. Vérifiez le PDF.`, { duration: 5000 });
    } catch (error) {
      console.error('Skip error:', error);
      toast.error('Erreur lors du passage de l\'étape');
    }
  };

  const handleStart = () => sendMessage("DÉMARRER");
  const handleContinue = () => sendMessage("Continuer");
  
  // Gestion du bouton Approfondir
  const handleDeepen = () => {
    if (isPaidUser) {
      // Pour Pro/Agence : ouvrir le modal de choix
      setShowDeepenModal(true);
    } else {
      // Pour gratuit : comportement simple (si applicable)
      sendMessage("Approfondir");
    }
  };

  // Option 1 : 10 insights supplémentaires
  const handleMoreInsights = async () => {
    setDeepenLoading(true);
    setShowDeepenModal(false);
    
    const token = localStorage.getItem('token');
    try {
      // Ajouter un message utilisateur
      const userMsg = { role: 'user', content: '🔍 Donne-moi 10 insights supplémentaires', timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, userMsg]);
      
      const response = await axios.post(`${API}/chat/deepen-insights`, {
        study_id: studyId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const aiMsg = { role: 'assistant', content: response.data.content, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, aiMsg]);
      toast.success('10 insights supplémentaires générés !');
    } catch (error) {
      console.error('Error getting insights:', error);
      toast.error('Erreur lors de la génération des insights');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setDeepenLoading(false);
    }
  };

  // Rediriger directement vers Stripe checkout pour passer au Pro
  const handleUpgradeToPro = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout/session`, {
        plan: 'pro_monthly',
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Rediriger vers Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la redirection vers le paiement');
      setLoading(false);
    }
  };

  // Option 2 : Poser une question
  const handleAskQuestion = async () => {
    if (!customQuestion.trim()) {
      toast.error('Veuillez entrer une question');
      return;
    }
    
    setDeepenLoading(true);
    setShowQuestionInput(false);
    setShowDeepenModal(false);
    
    const token = localStorage.getItem('token');
    try {
      // Ajouter un message utilisateur
      const userMsg = { role: 'user', content: `❓ ${customQuestion}`, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, userMsg]);
      
      const response = await axios.post(`${API}/chat/ask-question`, {
        study_id: studyId,
        question: customQuestion
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const aiMsg = { role: 'assistant', content: response.data.content, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, aiMsg]);
      setCustomQuestion('');
      toast.success('Réponse générée !');
    } catch (error) {
      console.error('Error asking question:', error);
      toast.error('Erreur lors de la génération de la réponse');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setDeepenLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted">Chargement...</div>
      </div>
    );
  }

  const currentPartInfo = getCurrentPartInfo(study?.current_step || 1);
  const globalProgress = Math.min(((study?.current_step || 0) / TOTAL_STEPS) * 100, 100);
  const groupedMessages = groupMessagesByPart(messages);
  
  // hasStarted = vrai si on a des messages OU si l'étude a progressé OU si on a une erreur en attente de retry
  const hasStarted = messages.length > 0 || (study?.current_step || 0) > 0 || showRetryButton;
  
  // Vérifier si l'utilisateur gratuit a atteint sa limite
  const isFreeUserLimited = subscription?.plan === 'free' && (study?.current_step || 0) >= FREE_STEPS_LIMIT;

  // Déterminer quelles parties sont accessibles
  const getPartStatus = (partId) => {
    // Pour les utilisateurs gratuits, toutes les parties sauf la 1 sont verrouillées visuellement
    if (subscription?.plan === 'free' && partId > 1) return 'locked-free';
    if (partId < currentPartInfo.partId) return 'completed';
    if (partId === currentPartInfo.partId) return 'active';
    return 'locked';
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Bannière pour utilisateurs gratuits ayant atteint la limite */}
      {isFreeUserLimited && (
        <div className="bg-gradient-to-r from-primary/20 to-accent/20 border-b border-primary/30">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎉</span>
                <div>
                  <p className="font-bold text-lg">Félicitations ! Tu as terminé ton essai gratuit !</p>
                  <p className="text-sm text-muted">Télécharge ton rapport puis choisis ton plan pour continuer :</p>
                </div>
              </div>
              
              {/* Bouton télécharger le PDF */}
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => handleDownloadPDF(1)}
                disabled={downloadingPdf || !canDownloadPDF}
                className={`border-primary text-primary hover:bg-primary/10 ${!canDownloadPDF ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {!canDownloadPDF ? <Lock className="w-4 h-4 mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                {!canDownloadPDF ? 'PDF non dispo en essai' : (downloadingPdf ? 'Téléchargement...' : 'Télécharger mon rapport PDF')}
              </Button>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/pricing?plan=pro">
                  <Button size="lg" className="shadow-glow-md whitespace-nowrap bg-primary hover:bg-primary/90">
                    <Crown className="w-4 h-4 mr-2" />
                    Plan Pro - 79€/mois
                  </Button>
                </Link>
                <Link to="/pricing?plan=agency">
                  <Button size="lg" variant="outline" className="whitespace-nowrap border-primary/50 hover:bg-primary/10">
                    <Building2 className="w-4 h-4 mr-2" />
                    Plan Agence - 199€/mois
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header compact */}
      <header className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
                  <Zap className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h1 className="font-semibold text-sm">{study?.title}</h1>
                  <p className="text-xs text-muted">Étape {study?.current_step || 0}/{TOTAL_STEPS} • {Math.round(globalProgress)}% complété</p>
                </div>
              </div>
            </div>
            <Button
              onClick={() => handleDownloadPDF(activePart)}
              disabled={downloadingPdf || !hasStarted || !canDownloadPDF}
              variant="outline"
              size="sm"
              title={!canDownloadPDF ? 'Les exports PDF ne sont pas disponibles pendant la période d\'essai' : ''}
            >
              {!canDownloadPDF ? <Lock className="w-4 h-4 mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              {!canDownloadPDF ? 'PDF bloqué' : `PDF Partie ${activePart}`}
            </Button>
          </div>
          
          {/* Barre de progression globale */}
          <div className="mt-2">
            <Progress value={globalProgress} className="h-1" />
          </div>
        </div>
      </header>

      {/* Navigation par parties (onglets) */}
      <div className="border-b border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide">
            {PARTS.map((part) => {
              const status = getPartStatus(part.id);
              const isActive = activePart === part.id;
              const messageCount = groupedMessages[part.id]?.length || 0;
              const isLockedForFree = status === 'locked-free';
              const isLocked = status === 'locked' || isLockedForFree;
              
              return (
                <button
                  key={part.id}
                  onClick={() => {
                    if (!isLocked) {
                      setActivePart(part.id);
                    }
                  }}
                  disabled={isLocked}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all
                    ${isActive 
                      ? 'bg-primary text-black' 
                      : status === 'completed'
                        ? 'bg-card hover:bg-muted/50 text-foreground'
                        : isLockedForFree
                          ? 'bg-card/30 text-muted/50 cursor-not-allowed border border-dashed border-muted/30'
                          : 'bg-card/50 text-muted cursor-not-allowed'
                    }
                  `}
                  style={isActive ? { backgroundColor: part.color } : {}}
                >
                  {status === 'completed' && !isActive && (
                    <CheckCircle className="w-4 h-4 text-primary" />
                  )}
                  {isLocked && (
                    <Lock className="w-4 h-4 text-muted/50" />
                  )}
                  <span className={isLockedForFree ? 'opacity-50' : ''}>{part.icon}</span>
                  <span className={isLockedForFree ? 'opacity-50' : ''}>Partie {part.id}: {part.fullName}</span>
                  {messageCount > 0 && !isLockedForFree && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-black/20' : 'bg-muted'}`}>
                      {messageCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Info de la partie active */}
      <div className="bg-card/30 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <span 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: PARTS[activePart - 1]?.color }}
              />
              <span className="font-medium">
                PARTIE {activePart}/5 : {PARTS[activePart - 1]?.fullName}
              </span>
            </div>
            {activePart === currentPartInfo.partId && (
              <span className="text-sm text-muted">
                Étape {currentPartInfo.stepInPart}/{currentPartInfo.totalStepsInPart}
              </span>
            )}
          </div>
          <Progress 
            value={activePart === currentPartInfo.partId 
              ? (currentPartInfo.stepInPart / currentPartInfo.totalStepsInPart) * 100 
              : activePart < currentPartInfo.partId ? 100 : 0
            } 
            className="h-1 mt-2" 
          />
        </div>
      </div>

      {/* Contenu de la partie active */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-[95vw] mx-auto px-4 py-6">
          {!hasStarted ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-xl font-semibold mb-3">Bienvenue dans votre étude marketing</h2>
              <p className="text-muted mb-6 max-w-md mx-auto text-sm">
                Je vais vous guider à travers 5 parties pour définir une stratégie marketing performante.
              </p>
              <Button onClick={handleStart} disabled={loading} className="shadow-glow-md">
                {loading ? 'Chargement...' : 'DÉMARRER L\'ANALYSE'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Messages de la partie active */}
              {groupedMessages[activePart]?.map((msg, i) => {
                // Détecter si c'est un message d'étape passée
                const isSkipMessage = msg.isSkipMessage || (msg.content && msg.content.includes('Étape') && msg.content.includes('passée'));
                
                return (
                <motion.div
                  key={msg.index || i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start w-full"
                >
                  <Card className={`p-4 w-full ${isSkipMessage ? 'bg-pink-500/20 border-2 border-pink-500' : 'bg-card border-border'}`}>
                    <div className="prose prose-invert prose-sm max-w-none">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          table: ({node, ...props}) => (
                            <div className="overflow-x-auto my-4 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
                              <table className="w-full border-collapse border border-border" {...props} />
                            </div>
                          ),
                          thead: ({node, ...props}) => (
                            <thead className="bg-muted/30" {...props} />
                          ),
                          th: ({node, ...props}) => (
                            <th className="border border-border px-3 py-2 text-left font-semibold text-sm align-top" {...props} />
                          ),
                          td: ({node, children, ...props}) => {
                            // Fonction pour traiter le contenu des cellules
                            const processContent = (content) => {
                              try {
                                if (typeof content !== 'string') return content;
                                
                                // Remplacer les <br> par des sauts de ligne
                                let processed = content.replace(/<br\s*\/?>/gi, '\n');
                                
                                // Détecter les patterns numérotés (1), 2), etc. ou 1. 2. etc.) et les convertir en liste
                                const lines = processed.split('\n');
                                const hasNumberedItems = lines.some(line => /^\d+[\)\.]\s/.test(line.trim()));
                                
                                if (hasNumberedItems) {
                                  return (
                                    <ol className="list-decimal list-inside space-y-1">
                                      {lines.map((line, idx) => {
                                        const trimmed = line.trim();
                                        if (!trimmed) return null;
                                        // Enlever le numéro au début si présent
                                        const cleanLine = trimmed.replace(/^\d+[\)\.]\s*/, '');
                                        return <li key={idx} className="text-sm">{cleanLine}</li>;
                                      }).filter(Boolean)}
                                    </ol>
                                  );
                                }
                                
                                // Sinon, juste séparer par lignes
                                if (processed.includes('\n')) {
                                  return lines.map((line, idx) => (
                                    <span key={idx}>
                                      {line}
                                      {idx < lines.length - 1 && <br />}
                                    </span>
                                  ));
                                }
                                
                                return processed;
                              } catch (e) {
                                return content;
                              }
                            };
                            
                            // Traiter les children de manière sécurisée
                            let processedChildren;
                            try {
                              processedChildren = Array.isArray(children) 
                                ? children.map((child, idx) => 
                                    typeof child === 'string' ? <span key={idx}>{processContent(child)}</span> : child
                                  )
                                : (typeof children === 'string' ? processContent(children) : children);
                            } catch (e) {
                              processedChildren = children;
                            }
                            
                            return (
                              <td className="border border-border px-3 py-2 text-sm align-top" {...props}>
                                {processedChildren}
                              </td>
                            );
                          },
                          tr: ({node, ...props}) => (
                            <tr className="hover:bg-muted/10" {...props} />
                          ),
                          p: ({node, children, ...props}) => {
                            // Vérifie si le paragraphe contient un titre d'étape
                            // Extraire tout le texte des children de manière sécurisée
                            const extractText = (child) => {
                              try {
                                if (typeof child === 'string') return child;
                                if (child?.props?.children) return extractText(child.props.children);
                                if (Array.isArray(child)) return child.map(extractText).join('');
                                return '';
                              } catch (e) {
                                return '';
                              }
                            };
                            const textContent = extractText(children);
                            const isStepTitle = /[🧩🎯💡🔥✨🚀📊🌟⭐]\s*ÉTAPE\s*\d+/i.test(textContent);
                            
                            if (isStepTitle) {
                              return <p className="mb-3 last:mb-0 text-xl font-bold text-pink-400" {...props}>{children}</p>;
                            }
                            return <p className="mb-3 last:mb-0" {...props}>{children}</p>;
                          },
                          strong: ({node, ...props}) => (
                            <strong className="font-semibold text-primary" {...props} />
                          ),
                          ul: ({node, ...props}) => (
                            <ul className="list-disc list-inside mb-3 space-y-1" {...props} />
                          ),
                          ol: ({node, ...props}) => (
                            <ol className="list-decimal list-inside mb-3 space-y-1" {...props} />
                          ),
                          h1: ({node, ...props}) => (
                            <h1 className="text-xl font-bold mb-3 mt-4" {...props} />
                          ),
                          h2: ({node, ...props}) => (
                            <h2 className="text-lg font-bold mb-2 mt-3" {...props} />
                          ),
                          h3: ({node, ...props}) => (
                            <h3 className="text-base font-semibold mb-2 mt-2" {...props} />
                          ),
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                    <div className="text-xs mt-2 opacity-50">
                      {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </Card>
                </motion.div>
              );
              })}
              
              {/* Message si partie vide */}
              {groupedMessages[activePart]?.length === 0 && activePart !== currentPartInfo.partId && (
                <div className="text-center py-10 text-muted">
                  <p>Cette partie n'a pas encore de contenu.</p>
                </div>
              )}
              
              {/* Indicateur de chargement */}
              {loading && activePart === currentPartInfo.partId && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <Card className="p-6 bg-card border-border">
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex gap-1">
                        <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">Analyse en cours...</p>
                        <p className="text-xs text-muted mt-1">Patience, cela peut prendre quelques minutes</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </main>

      {/* Paywall pour utilisateurs gratuits ayant terminé les 3 étapes */}
      {isFreeUserLimited && (
        <div className="border-t border-border bg-gradient-to-r from-primary/10 to-accent/10 sticky bottom-0">
          <div className="max-w-[95vw] mx-auto px-4 py-4">
            <div className="text-center mb-3">
              <p className="font-bold text-lg">🎉 Bravo ! Tu as terminé ton essai gratuit !</p>
              <p className="text-sm text-muted-foreground">Pour continuer ton étude, choisis ton plan :</p>
            </div>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to="/pricing?plan=pro">
                <Button className="shadow-glow-md bg-primary hover:bg-primary/90">
                  <Crown className="w-4 h-4 mr-2" />
                  Plan Pro - 79€/mois
                </Button>
              </Link>
              <Link to="/pricing?plan=agency">
                <Button variant="outline" className="border-primary/50 hover:bg-primary/10">
                  <Building2 className="w-4 h-4 mr-2" />
                  Plan Agence - 199€/mois
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Boutons d'action - seulement si pas limité */}
      {!isFreeUserLimited && study?.current_step < 47 && (
        <div className="border-t border-border bg-background/95 backdrop-blur-sm sticky bottom-0">
          <div className="max-w-[95vw] mx-auto px-4 py-3">
            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={handleContinue} disabled={loading} className="shadow-glow-md flex-1 max-w-xs">
                {loading ? 'Analyse en cours...' : 'Continuer'}
              </Button>
              <Button onClick={handleDeepen} disabled={loading} variant="outline" className="flex-1 max-w-xs">
                <Search className="w-4 h-4 mr-2" />
                Approfondir
              </Button>
              {/* Bouton Passer au payant pour les utilisateurs en essai sans CB ou avec code promo */}
              {(isTrialNoCB || isPromoNoCB) && (
                <Button 
                  onClick={handleUpgradeToPro} 
                  disabled={loading}
                  variant="outline" 
                  className="border-primary text-primary hover:bg-primary/10 flex-1 max-w-xs"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {loading ? 'Redirection...' : 'Passer au Pro'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Écran de fin d'étude - après étape 47 (fin partie 4) */}
      {hasStarted && !loading && study?.current_step >= 47 && !isFreeUserLimited && (
        <div className="border-t border-primary/30 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="max-w-[95vw] mx-auto px-4 py-8">
            <div className="flex flex-col items-center gap-6 text-center">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-2">
                  🎉 Félicitations ! Ton étude est terminée !
                </h2>
                <p className="text-muted max-w-md mx-auto">
                  Tu as complété les 4 parties principales de ton analyse stratégique. 
                  N'oublie pas de télécharger ton PDF !
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3 justify-center">
                <Button 
                  onClick={() => handleDownloadPDF(4)} 
                  className={!canDownloadPDF ? "opacity-50" : "shadow-glow-md"}
                  disabled={downloadingPdf || !canDownloadPDF}
                >
                  {!canDownloadPDF ? <Lock className="w-4 h-4 mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  {!canDownloadPDF ? 'PDF non dispo en essai' : 'Télécharger PDF Partie 4'}
                </Button>
                <Link to="/dashboard">
                  <Button variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
              
              {/* Option bonus */}
              <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg max-w-md">
                <p className="text-sm text-purple-300 mb-3">
                  <span className="font-bold">🦸 BONUS :</span> Tu peux aussi créer ta "Légende personnelle" avec le Voyage du Héros !
                </p>
                <Button 
                  onClick={handleContinue} 
                  variant="outline" 
                  className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                >
                  Commencer le Bonus
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Message pour utilisateurs gratuits limités */}
      {hasStarted && !loading && isFreeUserLimited && (
        <div className="border-t border-red-500/30 bg-red-500/5">
          <div className="max-w-[95vw] mx-auto px-4 py-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-lg font-semibold text-red-400">
                🔒 Pour débloquer le reste de ton étude et accéder aux 50 étapes complètes, passe à un plan payant !
              </p>
              <p className="text-sm text-muted">
                Tu as découvert un aperçu de ta stratégie. Imagine ce que tu pourrais accomplir avec l'analyse complète !
              </p>
              <Link to="/pricing">
                <Button className="shadow-glow-md bg-red-500 hover:bg-red-600 text-white">
                  Voir les offres et débloquer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Modal Approfondir pour Pro/Agence */}
      {showDeepenModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-bold">🔍 Approfondir cette étape</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowDeepenModal(false); setShowQuestionInput(false); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {!showQuestionInput ? (
              <div className="space-y-3">
                <Button 
                  onClick={handleMoreInsights}
                  disabled={deepenLoading}
                  className="w-full justify-start h-auto py-4 px-4"
                  variant="outline"
                >
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-primary mt-0.5" />
                    <div className="text-left">
                      <div className="font-semibold">10 insights supplémentaires</div>
                      <div className="text-sm text-muted font-normal">Obtenir plus de détails sur cette étape (ex: 10 douleurs en plus)</div>
                    </div>
                  </div>
                </Button>

                <Button 
                  onClick={() => setShowQuestionInput(true)}
                  disabled={deepenLoading}
                  className="w-full justify-start h-auto py-4 px-4"
                  variant="outline"
                >
                  <div className="flex items-start gap-3">
                    <MessageCircle className="w-5 h-5 text-accent mt-0.5" />
                    <div className="text-left">
                      <div className="font-semibold">Poser une question</div>
                      <div className="text-sm text-muted font-normal">Interroger l'IA sur un point précis de ton étude</div>
                    </div>
                  </div>
                </Button>

                <div className="pt-3 border-t border-border">
                  <Button 
                    onClick={() => { setShowDeepenModal(false); handleContinue(); }}
                    className="w-full"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Passer à l'étape suivante
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted block mb-2">Ta question :</label>
                  <Input
                    value={customQuestion}
                    onChange={(e) => setCustomQuestion(e.target.value)}
                    placeholder="Ex: Quelles sont les objections principales de mon avatar ?"
                    className="w-full"
                    onKeyDown={(e) => e.key === 'Enter' && handleAskQuestion()}
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowQuestionInput(false)} className="flex-1">
                    Retour
                  </Button>
                  <Button onClick={handleAskQuestion} disabled={!customQuestion.trim() || deepenLoading} className="flex-1">
                    Envoyer
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Loading overlay pour approfondissement */}
      {deepenLoading && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-xl p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
            <span>Génération en cours...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Study;
